/**
 * Per-highlight icon mapping. Each highlight card passes a small line
 * icon to render in the card's top-right corner, matching the Attio
 * overview pattern (content-specific icons differentiate cards
 * visually rather than leaving them as label-only rectangles).
 *
 * Keep the mapping declarative here so the per-type card components
 * stay lean and the icon vocabulary is consistent across kinds.
 */

import {
  Activity,
  BookOpen01,
  Briefcase01,
  Building07,
  Calendar,
  CalendarPlus01,
  Clock,
  CurrencyDollarCircle,
  File02,
  Hash01,
  Mail01,
  MarkerPin01,
  Phone,
  Tag01,
  User01,
} from '@untitledui/icons';
import type { ComponentType } from 'react';

import type { ComputedHighlightKind } from '../registry/entityTypeRegistry';

type IconComponent = ComponentType<{
  className?: string;
  'aria-hidden'?: 'true';
}>;

/**
 * Maps `ahoy_*` property names to the icon shown in the highlight
 * card's corner. Falls back to `Hash01` for unmapped properties so
 * the corner is never empty (visual consistency across the grid).
 */
export function iconForProperty(propertyName: string): IconComponent {
  const name = propertyName.toLowerCase();
  if (name.includes('email')) {
    return Mail01;
  }
  if (name.includes('phone')) {
    return Phone;
  }
  if (name.includes('job_title') || name.includes('title')) {
    return Briefcase01;
  }
  if (name.includes('company') || name.includes('domain')) {
    return Building07;
  }
  if (
    name.includes('city') ||
    name.includes('country') ||
    name.includes('region') ||
    name.includes('location') ||
    name.includes('address')
  ) {
    return MarkerPin01;
  }
  if (name.includes('amount') || name.includes('value')) {
    return CurrencyDollarCircle;
  }
  if (
    name.endsWith('_at') ||
    name.endsWith('_date') ||
    name.endsWith('_time')
  ) {
    return Clock;
  }
  return Hash01;
}

/**
 * Maps association-type names (e.g. `ahoy_contact_to_company`) to an
 * icon for the target entity type. Inferred from the suffix; we keep
 * this client-side because the highlight card doesn't have the schema
 * registry at hand.
 */
export function iconForAssociation(associationType: string): IconComponent {
  const name = associationType.toLowerCase();
  if (name.endsWith('_to_company')) {
    return Building07;
  }
  if (name.endsWith('_to_contact')) {
    return User01;
  }
  if (name.endsWith('_to_deal')) {
    return CurrencyDollarCircle;
  }
  if (name.endsWith('_to_lifecycle_stage') || name.includes('_stage')) {
    return Tag01;
  }
  if (name.endsWith('_to_owner') || name.endsWith('_to_user')) {
    return User01;
  }
  if (name.endsWith('_to_meeting')) {
    return Calendar;
  }
  return Tag01;
}

/** Computed-highlight kind → icon. */
export function iconForComputed(kind: ComputedHighlightKind): IconComponent {
  switch (kind) {
    case 'company_location':
      return MarkerPin01;
    case 'meeting_duration':
      return Clock;
    case 'company_open_deal_value':
      return CurrencyDollarCircle;
    case 'contact_last_met':
      return Calendar;
    case 'next_interaction':
      return CalendarPlus01;
    case 'contact_touchpoints_30d':
      return Activity;
    case 'meeting_prep_status':
      return BookOpen01;
    case 'meeting_summary_status':
      return File02;
    default: {
      const _exhaustive: never = kind;
      void _exhaustive;
      return Hash01;
    }
  }
}

/** Default icons for the simpler card kinds. */
export const LAST_EVENT_ICON: IconComponent = Mail01;
export const LAST_PROPERTY_UPDATE_ICON: IconComponent = Clock;
export const ENTITY_COUNT_ICON: IconComponent = Activity;
